import {
  CButton,
  CCol,
  CInput,
  CLabel,
  CRow,
  CTextarea,
} from "@coreui/react";
import React, { useCallback, useEffect, useState } from "react";
import Errors, { errorsAreSame, getFieldErrors } from "../../models/errors";
import { FieldErrors } from "../form/FieldErrors";
import {
  emptyValueOnUndefined,
  emptyValueOnUndefinedOrNull,
} from "../../utils/fields";
import { RemissionNoteItem } from "../../models/remission-note";
import Client, { CLIENT_TYPE_GOVERNMENT } from "../../models/client";
interface remissionNoteItemFormProps {
  item: RemissionNoteItem;
  client: Client | null;
  onDelete: (_: RemissionNoteItem) => void;
  onChange: (_: RemissionNoteItem) => void;
  errors: Errors;
}

const addMissingValues = (item: RemissionNoteItem) => {
  const newItem = { ...item };
  // TODO: see how to handle this
  newItem.measureUnit = 77;
  newItem.code = "001";

  return newItem;
};

const RemissionNoteItemForm: React.FC<remissionNoteItemFormProps> = ({
  item,
  client,
  onDelete,
  onChange,
  errors,
}) => {
  const [editingItem, setEditingItem] = useState(item);
  const onDeleteClick = useCallback(() => {
    onDelete(editingItem);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingItem]);

  const onQuantityChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let newItem = { ...editingItem };
      if (!isNaN(parseFloat(e.target.value))) {
        newItem.quantity = parseFloat(e.target.value);
      } else {
        newItem.quantity = undefined;
      }

      newItem = addMissingValues(newItem);

      setEditingItem(newItem);
      onChange(newItem);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editingItem]
  );

  const onDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let newItem = { ...editingItem };
      newItem.description = e.target.value;

      newItem = addMissingValues(newItem);

      setEditingItem(newItem);
      onChange(newItem);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editingItem]
  );

  const onDncpLevelCodeGeneralChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newItem = { ...editingItem };
      newItem.dncpLevelCodeGeneral = e.target.value;
      setEditingItem(newItem);
      onChange(newItem);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editingItem]
  );

  const onDncpLevelCodeSpecificChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newItem = { ...editingItem };
      newItem.dncpLevelCodeSpecific = e.target.value;
      setEditingItem(newItem);
      onChange(newItem);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editingItem]
  );

  useEffect(() => {
    setEditingItem(item);
  }, [item]);

  const itemLabelStyle: React.CSSProperties = {
    fontWeight: 600,
    fontSize: "0.8rem",
    color: "var(--theme-muted)",
    marginBottom: "3px",
    display: "block",
  };

  return (
    <div
      style={{
        position: "relative",
        border: "1px solid var(--theme-border)",
        borderLeft: "4px solid #006657",
        borderRadius: "6px",
        marginBottom: "10px",
        padding: "12px 14px 10px",
        background: "var(--theme-surface)",
      }}
    >
      {/* Botón eliminar: siempre arriba a la derecha */}
      <div style={{ position: "absolute", top: "10px", right: "12px" }}>
        <CButton
          color="danger"
          variant="outline"
          size="sm"
          onClick={onDeleteClick}
        >
          <i className="fa fa-trash"></i>
        </CButton>
      </div>

      {/* Fila 1: Cantidad | Detalle */}
      <CRow className="align-items-end mb-2" style={{ paddingRight: "46px" }}>
        <CCol xs={4} md={1}>
          <CLabel style={itemLabelStyle}>
            <span className="text-danger">*</span> Cant.
          </CLabel>
          <CInput
            type="number"
            step="0.001"
            value={emptyValueOnUndefined(editingItem.quantity)}
            onChange={onQuantityChange}
          ></CInput>
          <FieldErrors
            errors={getFieldErrors("quantity", errors) as string[]}
          ></FieldErrors>
        </CCol>
        <CCol xs={8} md={11}>
          <CLabel style={itemLabelStyle}>
            <span className="text-danger">*</span> Detalle
          </CLabel>
          <CTextarea
            placeholder="Detalle del ítem"
            value={emptyValueOnUndefined(editingItem.description)}
            onChange={onDescriptionChange}
            style={{ resize: "vertical", minHeight: "60px" }}
          ></CTextarea>
          <FieldErrors
            errors={getFieldErrors("description", errors) as string[]}
          ></FieldErrors>
        </CCol>
      </CRow>

      {/* DNCP (solo para clientes gubernamentales) */}
      {client !== null && client.type === CLIENT_TYPE_GOVERNMENT && (
        <>
          <hr style={{ margin: "10px 0", borderColor: "#f9b115", borderWidth: "2px" }} />
          <CRow className="align-items-end">
            <CCol xs={6} md={3}>
              <CLabel style={itemLabelStyle}>
                <span className="text-danger">*</span> Nivel DNCP General
              </CLabel>
              <CInput
                type="text"
                placeholder="Nivel general"
                onChange={onDncpLevelCodeGeneralChange}
                value={emptyValueOnUndefinedOrNull(editingItem.dncpLevelCodeGeneral)}
              ></CInput>
              <FieldErrors
                errors={getFieldErrors("dncpLevelCodeGeneral", errors) as string[]}
              ></FieldErrors>
            </CCol>
            <CCol xs={6} md={3}>
              <CLabel style={itemLabelStyle}>
                <span className="text-danger">*</span> Nivel DNCP Específico
              </CLabel>
              <CInput
                type="text"
                placeholder="Nivel específico"
                onChange={onDncpLevelCodeSpecificChange}
                value={emptyValueOnUndefinedOrNull(editingItem.dncpLevelCodeSpecific)}
              ></CInput>
              <FieldErrors
                errors={getFieldErrors("dncpLevelCodeSpecific", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </>
      )}
    </div>
  );
};

const propsAreEqual = (
  prevItemProps: remissionNoteItemFormProps,
  nextItemProps: remissionNoteItemFormProps
): boolean => {
  return (
    prevItemProps.item.id === nextItemProps.item.id &&
    prevItemProps.item.obs === nextItemProps.item.obs &&
    prevItemProps.item.measureUnit === nextItemProps.item.measureUnit &&
    prevItemProps.item.description === nextItemProps.item.description &&
    prevItemProps.item.quantity === nextItemProps.item.quantity &&
    errorsAreSame(prevItemProps.errors, nextItemProps.errors)
  );
};

export default React.memo(RemissionNoteItemForm, propsAreEqual);
